import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import axios from '../utils/api';
import { COUNTRY_ISD_CODES, getPhoneRuleByIsd } from '../constants/countryIsdCodes';
import { useAuth } from '../contexts/AuthContext';

const BuyerProfile = () => {
  const { refreshCurrentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
  });
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    country_isd_code: '+91',
    phone_number: '',
    marketing_phone_opt_in: true,
    marketing_email_opt_in: true,
    marketing_whatsapp_opt_in: true,
    marketing_sms_opt_in: true,
    marketing_enabled: true,
  });

  const phoneRule = useMemo(
    () => getPhoneRuleByIsd(formData.country_isd_code),
    [formData.country_isd_code]
  );

  const phoneLengthHint = phoneRule.minLength === phoneRule.maxLength
    ? `${phoneRule.minLength} digits`
    : `${phoneRule.minLength}-${phoneRule.maxLength} digits`;

  const formatIstDateOnly = (value) => {
    if (!value) {
      return 'N/A';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return 'N/A';
    }

    return parsed.toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError('');
      setFieldErrors({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
      });
      const response = await axios.get('/api/auth/me');
      const userData = response.data;
      setProfile(userData);
      setFormData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        email: userData.email || '',
        country_isd_code: userData.country_isd_code || '+91',
        phone_number: userData.phone_number || '',
        marketing_phone_opt_in: userData.marketing_phone_opt_in ?? true,
        marketing_email_opt_in: userData.marketing_email_opt_in ?? true,
        marketing_whatsapp_opt_in: userData.marketing_whatsapp_opt_in ?? true,
        marketing_sms_opt_in: userData.marketing_sms_opt_in ?? true,
        marketing_enabled: userData.marketing_enabled ?? userData.marketing_opt_out ?? true,
      });
    } catch (loadError) {
      setError(loadError.response?.data?.detail || 'Unable to load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const handlePhoneChange = (event) => {
    const numericValue = event.target.value.replace(/\D/g, '').slice(0, phoneRule.maxLength);
    setFormData((current) => ({ ...current, phone_number: numericValue }));
    setFieldErrors((current) => ({ ...current, phone_number: '' }));
  };

  const handleTextInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    if (Object.prototype.hasOwnProperty.call(fieldErrors, name)) {
      setFieldErrors((current) => ({ ...current, [name]: '' }));
    }
  };

  const handleToggleChange = (event) => {
    const { name, checked } = event.target;
    setFormData((current) => ({ ...current, [name]: checked }));
  };

  const validatePhone = () => {
    if (!formData.phone_number) {
      return 'Phone number is required';
    }

    if (formData.phone_number.length < phoneRule.minLength || formData.phone_number.length > phoneRule.maxLength) {
      return `Phone number for ${formData.country_isd_code} must be ${phoneLengthHint}`;
    }

    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setFieldErrors({
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
    });

    const phoneValidationError = validatePhone();
    if (phoneValidationError) {
      setFieldErrors((current) => ({ ...current, phone_number: phoneValidationError }));
      return;
    }

    const firstName = (formData.first_name || '').trim();
    const lastName = (formData.last_name || '').trim();
    const email = (formData.email || '').trim().toLowerCase();

    if (!firstName || !lastName) {
      setFieldErrors((current) => ({
        ...current,
        first_name: !firstName ? 'First name is required' : '',
        last_name: !lastName ? 'Last name is required' : '',
      }));
      return;
    }
    if (!email) {
      setFieldErrors((current) => ({ ...current, email: 'Email is required' }));
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...formData,
        first_name: firstName,
        last_name: lastName,
        email,
      };
      const response = await axios.put('/api/auth/me', payload);
      setProfile(response.data.user || null);
      setSuccess('Profile and communication preferences updated successfully');
      await refreshCurrentUser();
    } catch (saveError) {
      const detail = saveError.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Unable to save profile updates';

      if (message === 'Email already registered') {
        setFieldErrors((current) => ({ ...current, email: message }));
      } else if (message === 'Phone number already registered' || message.includes('Phone number for')) {
        setFieldErrors((current) => ({ ...current, phone_number: message }));
      } else if (message === 'Phone number is required') {
        setFieldErrors((current) => ({ ...current, phone_number: message }));
      } else if (message === 'Name fields cannot be empty') {
        setFieldErrors((current) => ({
          ...current,
          first_name: 'First name is required',
          last_name: 'Last name is required',
        }));
      } else {
        setError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={8}>
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body>
              <h2 className="h4 mb-3">My Registration Information</h2>
              <p className="mb-1"><strong>Name:</strong> {profile?.name || 'Not set'}</p>
              <p className="mb-1"><strong>Email:</strong> {profile?.email || 'Not set'}</p>
              <p className="mb-0"><strong>Registered On:</strong> {formatIstDateOnly(profile?.created_at)}</p>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-0">
            <Card.Body>
              <h2 className="h4 mb-3">Contact And Communication Preferences</h2>

              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="firstName">
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleTextInputChange}
                        isInvalid={Boolean(fieldErrors.first_name)}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {fieldErrors.first_name}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="lastName">
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleTextInputChange}
                        isInvalid={Boolean(fieldErrors.last_name)}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {fieldErrors.last_name}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3" controlId="email">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleTextInputChange}
                    isInvalid={Boolean(fieldErrors.email)}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.email}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Email must be unique across all users.
                  </Form.Text>
                </Form.Group>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="countryIsdCode">
                      <Form.Label>Country ISD Code</Form.Label>
                      <Form.Select
                        value={formData.country_isd_code}
                        onChange={(event) => {
                          const selectedIsd = event.target.value;
                          const selectedRule = getPhoneRuleByIsd(selectedIsd);
                          setFormData((current) => ({
                            ...current,
                            country_isd_code: selectedIsd,
                            phone_number: current.phone_number.slice(0, selectedRule.maxLength),
                          }));
                        }}
                      >
                        {COUNTRY_ISD_CODES.map((entry) => (
                          <option key={entry.code} value={entry.code}>
                            {entry.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={8}>
                    <Form.Group className="mb-3" controlId="phoneNumber">
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control
                        type="tel"
                        value={formData.phone_number}
                        onChange={handlePhoneChange}
                        maxLength={phoneRule.maxLength}
                        placeholder={`Enter ${phoneLengthHint}`}
                        isInvalid={Boolean(fieldErrors.phone_number)}
                      />
                      <Form.Control.Feedback type="invalid">
                        {fieldErrors.phone_number}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Required for {formData.country_isd_code}: {phoneLengthHint}. Country code + phone must be unique.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Card className="mb-3 border-light bg-light">
                  <Card.Body>
                    <h3 className="h6 mb-3">Preferred Channel</h3>

                    <Form.Group className="mb-2" controlId="marketingPhoneOptIn">
                      <Form.Check
                        type="switch"
                        name="marketing_phone_opt_in"
                        checked={formData.marketing_phone_opt_in}
                        onChange={handleToggleChange}
                        label="Phone 📞"
                      />
                    </Form.Group>

                    <Form.Group className="mb-2" controlId="marketingEmailOptIn">
                      <Form.Check
                        type="switch"
                        name="marketing_email_opt_in"
                        checked={formData.marketing_email_opt_in}
                        onChange={handleToggleChange}
                        label="Email ✉"
                      />
                    </Form.Group>

                    <Form.Group className="mb-2" controlId="marketingWhatsappOptIn">
                      <Form.Check
                        type="switch"
                        name="marketing_whatsapp_opt_in"
                        checked={formData.marketing_whatsapp_opt_in}
                        onChange={handleToggleChange}
                        label="WhatsApp 💬"
                      />
                    </Form.Group>

                    <Form.Group className="mb-0" controlId="marketingSmsOptIn">
                      <Form.Check
                        type="switch"
                        name="marketing_sms_opt_in"
                        checked={formData.marketing_sms_opt_in}
                        onChange={handleToggleChange}
                        label="SMS 📨"
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>

                <Form.Group className="mb-3" controlId="marketingOptOut">
                  <Form.Check
                    type="switch"
                    name="marketing_enabled"
                    checked={formData.marketing_enabled}
                    onChange={handleToggleChange}
                    label="Opt out of product updates, offers, and new-feature messages"
                  />
                  <Form.Text className="text-muted">
                    When enabled, promotional communications are allowed on selected preferred channels.
                    Disable this switch to opt out.
                  </Form.Text>
                </Form.Group>

                <Alert variant="info" className="mb-3">
                  Transactional messages cannot be opted out. The system uses Email and SMS by default for
                  purchase status, shipping status, payment/OTP verification, and password-change alerts,
                  and may also use any additional preferred channels enabled above.
                </Alert>

                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Preferences'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BuyerProfile;
